import React, { useEffect, useState, useCallback, useMemo } from 'react'
import './GroceryFeed.scss'
import useSignalRFeed from '../../hooks/useSignalrFeed'
import { WebSocketInvocation } from '../../hooks/webSocketInvocation'
import config from '../../config'
import { filterFeed, FilterOption, getFilterOptions, } from './filterAndSortFeedItems'
import { get, mergePatch, post, put, remove } from '../../utils/http'
import { TOGO_CHANNEL_HEADER_VALUE } from '../../constants/channelConstants'
import { useDeviceContext } from '../../contexts/device-context'
import { useAlertContext } from '../../contexts/alert-context'
import { Store, useStoreContext } from '../../contexts/store-context'
import StoreGroceryStock from '../../types/StoreGroceryStock'
import { StoreActiveCategories, IPicklist, Item } from '../../types/Picklist'
import { CustomPrice } from '../../types/CustomPrice'
import { IcaButtonIcon } from '../IcaElements/IcaButtonIcon'
import { IcaDialog } from '../IcaElements/IcaDialog'
import { IcaAttentionHeader } from '../IcaElements/IcaAttentionHeader'
import { IcaButton } from '../IcaElements/IcaButton'
import { IcaHeader } from '../IcaElements/IcaHeader'
import { IcaSegment } from '../IcaElements/IcaSegment'
import { IcaCardRow } from '../IcaElements/IcaCardRow'
import { IcaInputNumberStepper } from '../IcaElements/IcaInputNumberStepper'
import Picklist from '../Picklist/Picklist'
import GroceryFeedCleanup from '../GroceryFeedCleanup/GroceryFeedCleanup'
import GroceryFeedItemList from '../GroceryFeedItemList/GroceryFeedItemList'
import GroceryFeedTable from './GroceryFeedTable'
import SpinnerIcon from '../Icons/SpinnerIcon'
import '@ica-azure/ica-elements/IcaIconShoppingList'
import '@ica-azure/ica-elements/IcaIconTrash'
import '@ica-azure/ica-elements/IcaIconNewWindow'
import { IcaInputSearch } from '../IcaElements/IcaInputSearch'
import SearchPlaceholder from '../SearchPlaceholder/SearchPlaceholder'

export default function GroceryFeed() {
  const [feed, setFeed] = useState<Array<StoreGroceryStock>>([])
  const [filteredFeed, setFilteredFeed] = useState<Array<StoreGroceryStock>>([])
  const [filterOptions, setFilterOptions] = useState<Array<FilterOption>>([])
  const [selectedFilterOption, setSelectedFilterOption] = useState(filterOptions[0])
  const [activeFilter, setActiveFilter] = useState<'all' | 'available' | 'almostOut' | 'out' | 'cheap'>('all')
  const [search, setSearch] = useState<string>('')
  const [selectedGrocery, setSelectedGrocery] = useState<StoreGroceryStock>()
  const { deviceState } = useDeviceContext()
  const { alert } = useAlertContext()
  const { storeState } = useStoreContext()
  const storeId = storeState.selectedStore?.id
  const [confirmDeleteSingleModalOpen, setConfirmDeleteSingleModalOpen] = useState(false)
  const [confirmDeleteMultipleModalOpen, setConfirmDeleteMultipleModalOpen] = useState(false)

  // Picklist
  const [picklistModalOpen, setPicklistModalOpen] = useState(false)
  const [items, setItems] = React.useState<Item[]>([])
  const [categories, setCategories] = React.useState<StoreActiveCategories[]>([])
  const [sites, setSites] = React.useState<Store[]>([])
  const [picklistLoading, setPicklistLoading] = React.useState<boolean>(true)
  const [picklistError, setPicklistError] = React.useState<boolean>(false)
  
  // GroceryFeedCleanup
  const [cleanupModalOpen, setCleanupModalOpen] = useState(false)
  const [cleanupLoading, setCleanupLoading] = React.useState<boolean>(false)
  const [selectedArticles, setSelectedArticles] = useState<Array<string>>([])
  const [allArticles, setAllArticles] = useState<Array<string>>([])

  // RunningOutThreshold
  const [confirmRunningOutThresholdModalOpen, setConfirmRunningOutThresholdModalOpen] = useState(false)
  const [updatedRunningOutThreshold, setUpdatedRunningOutThreshold] = useState<number>(1)
  const [thresholdInputError, setThresholdInputError] = useState<string | null>(null)

  const handleGroceryStocksUpdate = useCallback((updatedFeedItems: Array<StoreGroceryStock>): void => {
    setFeed((prevFeedState: Array<StoreGroceryStock>) => {
      const newFeed: StoreGroceryStock[] = [...prevFeedState]
      for (const updatedItem of updatedFeedItems) {
        const existingItemIndex: number = newFeed.findIndex(
          (i) => i.consumerItemId == updatedItem.consumerItemId
        )
        if (existingItemIndex !== -1) {
          newFeed[existingItemIndex] = updatedItem
        } else {
          newFeed.push(updatedItem)
        }
      }
      return newFeed
    })
  }, [])

  const handleGroceryStockRemoval = useCallback((removedItemIdentifier: string): void => {
    setFeed((prevFeedState: Array<StoreGroceryStock>) => {
      return prevFeedState.filter((item) => item.ean !== removedItemIdentifier)
    })
  }, [])

  const invocations: Array<WebSocketInvocation<any>> = useMemo(() => [
    {
      name: 'UpsertStoreGroceryStock',
      action: async (updatedFeedItem: StoreGroceryStock) =>
        handleGroceryStocksUpdate([updatedFeedItem]),
    },
    {
      name: 'UpsertStoreGroceryStocks',
      action: async (updatedFeedItems: Array<StoreGroceryStock>) =>
        handleGroceryStocksUpdate(updatedFeedItems),
    },
    {
      name: 'RemoveStoreGroceryStock',
      action: async (removedItemIdentifier: string) =>
        handleGroceryStockRemoval(removedItemIdentifier),
    },
  ], [handleGroceryStocksUpdate, handleGroceryStockRemoval])

  const { initialFeed, loading } = useSignalRFeed<StoreGroceryStock>(
    config.GROCERY_STOCK_FEED_SIGNALR_URL,
    `${config.GROCERY_STOCK_API_BASE_URL}/v1/grocery-stocks`,
    invocations
  )

  // Filtering
  const changeFilter = (e: React.FormEvent<HTMLButtonElement>) => {
    const value = e.currentTarget.value
    const newFilterOption = filterOptions.find((o) => o.value === value)
    if (newFilterOption !== undefined) {
      setActiveFilter(newFilterOption.value)
      setSelectedFilterOption(newFilterOption)
      setSelectedArticles([])
    } else {
      throw new Error(
        'Filter selection: Selected value not found in given options array'
      )
    }
  }

  const handleSearchChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.currentTarget.value)
  }
  
  const handleSearchClear = () => {
    setSearch('')
  }

  const handleCustomPriceDelete = useCallback(async (item: StoreGroceryStock): Promise<void> => {
    if (!item?.customPrice) {
      return
    }

    try {
      await remove(
        `${config.GROCERY_API_BASE_URL}/v1/groceries/custom-prices/${item.storeId}/${TOGO_CHANNEL_HEADER_VALUE}/${item.consumerItemId}`
      )

      const newFeed = [...feed]
      const itemToUpdate = newFeed.find((i) => i.consumerItemId === item.consumerItemId)!
      itemToUpdate.customPrice = undefined
      setFeed(newFeed)
      alert.success('Priset har återställts.')
    } catch {
      alert.error('Något gick fel när priset skulle återställas.')
    }
  }, [feed, alert])

  const handleCustomPriceSave = useCallback(async (item: StoreGroceryStock, newValue: number | null | undefined): Promise<void> => {
    if (newValue === undefined) {
      return
    } else if (newValue === null) {
      if (item.customPrice) {
        await handleCustomPriceDelete(item)
      }
      return
    } else if (newValue === item.customPrice) {
      return
    }

    const customerPriceToUpsert: CustomPrice = {
      articleIdentifier: item.consumerItemId ?? '',
      storeId: item.storeId,
      channelName: TOGO_CHANNEL_HEADER_VALUE,
      price: newValue,
    }

    let upsertedCustomPrice: CustomPrice
    try {
      if (!item?.customPrice) {
        upsertedCustomPrice = await post(`${config.GROCERY_API_BASE_URL}/v1/groceries/custom-prices`, customerPriceToUpsert)
      } else {
        upsertedCustomPrice = await put(
          `${config.GROCERY_API_BASE_URL}/v1/groceries/custom-prices/${item.storeId}/${TOGO_CHANNEL_HEADER_VALUE}/${item.consumerItemId}`,
          customerPriceToUpsert
        )
      }

      const newFeed = [...feed]
      const itemToUpdate = newFeed.find((i) => i.consumerItemId === item.consumerItemId)!
      itemToUpdate.customPrice = upsertedCustomPrice.price
      setFeed(newFeed)
      alert.success('Priset har sparats.')
    } catch {
      alert.error('Något gick fel när priset skulle sparas.')
    }
  }, [feed, alert, handleCustomPriceDelete])

  const deleteGrocery = async (grocery: StoreGroceryStock | undefined) => {
    if (grocery === undefined) {
      return
    }

    try {
      await remove<any>(
        `${config.GROCERY_STOCK_API_BASE_URL}/v1/grocery-stocks/${grocery.storeId}/${grocery.ean}`
      )

      setFeed(prevFeed => {
        const updatedFeed = prevFeed.filter((item) => item.consumerItemId !== grocery.consumerItemId)
        setFilterOptions(getFilterOptions(updatedFeed))
        return updatedFeed
      })
      alert.success('Artikeln har rensats från sortimentet')
    } catch {
      alert.error('Något gick fel när artikeln skulle rensas')
    } finally {
      setSelectedGrocery(undefined)
    }
  }

  const deleteMultipleGroceries = async (eansToDelete: string[]): Promise<void> => {
    if (eansToDelete.length === 0 || !storeId) {
      return
    }
    setCleanupLoading(true)

    const deletePromises = eansToDelete.map(ean =>
      remove<any>(
        `${config.GROCERY_STOCK_API_BASE_URL}/v1/grocery-stocks/${storeId}/${ean}`
      ).then(() => ({ ean, success: true }))
        .catch((error) => {
          console.log(`Failed to delete article ${ean}:`, error)
          return { ean, success: false }
        })
    )

    const results = await Promise.all(deletePromises)
    
    // Get successfully deleted EANs
    const successfullyDeletedEans = results
      .filter(result => result.success)
      .map(result => result.ean)

    const successCount = successfullyDeletedEans.length
    const failedCount = results.length - successCount

    // Update feed for successfully deleted items
    if (successCount > 0) {
      setFeed(prevFeed => {
        const updatedFeed = prevFeed.filter(item => !successfullyDeletedEans.includes(item.ean))
        setFilterOptions(getFilterOptions(updatedFeed))
        return updatedFeed
      })
    }

    setCleanupLoading(false)

    if (failedCount === 0) {
      alert.success(`${successCount} artik${successCount > 1 ? 'lar' : 'el'} rensad${successCount > 1 ? 'e' : ''} från sortimentet`)
    } else if (successCount > 0) {
      alert.warning(`${successCount} artik${successCount > 1 ? 'lar' : 'el'} rensad${successCount > 1 ? 'e' : ''} från sortimentet, men ${failedCount} st misslyckades`)
    } else {
      alert.error(`Något gick fel när artik${successCount > 1 ? 'larna' : 'eln'} skulle rensas`)
    }
  }

  const handleSelectArticle = useCallback((ean: string) => {
    if (selectedArticles.includes(ean)) {
      setSelectedArticles(selectedArticles.filter(selectedEan => selectedEan !== ean))
    } else {
      setSelectedArticles([...selectedArticles, ean])
    }
  }, [selectedArticles])

  // Effects
  useEffect(() => {
    setFeed([...initialFeed])
  }, [initialFeed])
  
  useEffect(() => {
    const searchText = search.trim().toLocaleLowerCase()
    const searchResults = searchText
      ? feed.filter(item =>
        item.name.toLocaleLowerCase().includes(searchText) ||
        item.ean.toLocaleLowerCase().includes(searchText)
      )
      : feed

    setFilterOptions(getFilterOptions(searchResults))
    setFilteredFeed(filterFeed(searchResults, selectedFilterOption?.value))
  }, [feed, selectedFilterOption?.value, search])

  useEffect(() => {
    setAllArticles(filteredFeed.map(item => item.ean))
  }, [filteredFeed])

  // Fetch picklist data whenever the picklist dialog is opened
  const fetchPicklistData = React.useCallback(async () => {
    setPicklistLoading(true)
    setPicklistError(false)

    if (!storeState.selectedStore?.id) {
      setPicklistLoading(false)
      return
    }

    try {
      const { parentStoreId, id: storeId } = storeState.selectedStore

      let sites: Store[] = []
      if (parentStoreId) {
        sites = await get<Store[]>(`${config.STORE_API_NEW_BASE_URL}/v1/stores?parentStoreId=${parentStoreId}`)
      } else {
        sites = [storeState.selectedStore]
      }

      if (sites.length > 0) {
        setSites(sites.filter(site => site.active))
        const picklistData = await get<IPicklist>(
          `${config.GROCERY_STOCK_API_BASE_URL}/v1/pick-lists${parentStoreId ? `/${parentStoreId}` : `?storeIds=${storeId}`}`
        )

        if (picklistData?.items && picklistData?.storeActiveCategories) {
          setItems([...picklistData.items])
          setCategories(picklistData.storeActiveCategories)
        }
      }
    } catch (error) {
      console.log('Error fetching picklist data', error)
      setPicklistError(true)
    }
    setPicklistLoading(false)
  }, [storeState.selectedStore])

  React.useEffect(() => {
    if (picklistModalOpen) {
      fetchPicklistData()
    }
  }, [picklistModalOpen, fetchPicklistData])

  const applyRunningOutThreshold = useCallback((threshold: number | null, matchesItem: (i: StoreGroceryStock) => boolean) => {
    setFeed(prev => prev.map(i => matchesItem(i) ? { ...i, runningOutThreshold: threshold } : i))
  }, [])

  const applyMaxQuantity = useCallback((maxQuantity: number | null, matchesItem: (i: StoreGroceryStock) => boolean) => {
    setFeed(prev => prev.map(i => matchesItem(i) ? { ...i, maxQuantity: maxQuantity } : i))
  }, [])

  const onChangeStockSettings = useCallback(async (item: StoreGroceryStock, threshold?: number, maxQuantity?: number): Promise<void> => {
    if (!item || !storeId) return

    const thresholdToSend = threshold === undefined ? null : threshold
    const maxQuantityToSend = maxQuantity === undefined ? null : maxQuantity

    const currentItemThreshold = item.runningOutThreshold == null ? null : item.runningOutThreshold
    const currentItemMaxQuantity = item.maxQuantity == null ? null : item.maxQuantity
    const thresholdChanged = thresholdToSend !== currentItemThreshold
    const maxQuantityChanged = maxQuantityToSend !== currentItemMaxQuantity

    if (!thresholdChanged && !maxQuantityChanged) return

    const body: any = {
      runningOutThreshold: thresholdToSend,
      maxQuantity: maxQuantityToSend,
    }

    try {
      await mergePatch<any, any>(
        `${config.GROCERY_STOCK_API_BASE_URL}/v1/grocery-stocks/${item.storeId}/${item.ean}`,
        body
      )

      applyRunningOutThreshold(body.runningOutThreshold, i => i.consumerItemId === item.consumerItemId)
      applyMaxQuantity(body.maxQuantity, i => i.consumerItemId === item.consumerItemId)
      alert.success('Saldoinställningar har sparats')
    } catch (e) {
      alert.error('Något gick fel när saldoinställningar skulle sparas')
      console.log('Failed to update grocery stock settings for item', item, e)
    }
  }, [storeId, alert, applyRunningOutThreshold, applyMaxQuantity])
  
  // Update running out threshold for selected articles
  const updateRunningOutThresholdMultipleArticles = useCallback(async (eansToUpdate: string[]): Promise<void> => {
    if (eansToUpdate.length === 0 || !storeId) {
      return
    }

    if (updatedRunningOutThreshold != null && updatedRunningOutThreshold <= 0) {
      setThresholdInputError('Gränsvärdet måste vara större än 0')
      return
    }

    const body = {
      runningOutThreshold: updatedRunningOutThreshold,
    }

    const updatePromises = eansToUpdate.map(ean =>
      mergePatch<any, any>(
        `${config.GROCERY_STOCK_API_BASE_URL}/v1/grocery-stocks/${storeId}/${ean}`,
        body
      ).then(() => ({ ean, success: true }))
        .catch((error) => {
          alert.error(`Något gick fel när gränsvärdet skulle sparas för artikel ${ean}`)
          console.log(`Failed to update article ${ean}:`, error)
          return { ean, success: false }
        })
    )

    const results = await Promise.all(updatePromises)
    
    // Get successfully updated EANs
    const successfullyUpdatedEans = results
      .filter(result => result.success)
      .map(result => result.ean)

    if (successfullyUpdatedEans.length > 0) {
      const updatedSet = new Set(successfullyUpdatedEans)
      applyRunningOutThreshold(body.runningOutThreshold, i => updatedSet.has(i.consumerItemId as string))
      alert.success(`Gränsvärdet har sparats för ${successfullyUpdatedEans.length} artik${successfullyUpdatedEans.length > 1 ? 'lar' : 'el'}`)
    }

    setConfirmRunningOutThresholdModalOpen(false)
    setSelectedArticles([])
    setUpdatedRunningOutThreshold(1)
    setThresholdInputError(null)
  }, [storeId, updatedRunningOutThreshold, applyRunningOutThreshold])

  return (
    <>          
      {loading && (
        <div className='feed-spinner'>
          <SpinnerIcon />{' '}
        </div>
      )}
      {feed && (
        <>
          {deviceState.isMobile && <div className='absolute right-4 top-[119px] flex items-center gap-2'>
            <IcaButtonIcon
              accent
              text='Plocklista'
              onClick={() => setPicklistModalOpen(true)}
            >
              <tgdb-ica-icon-shopping-list />
            </IcaButtonIcon>
            <IcaButtonIcon
              accent
              text='Rensa'
              onClick={() => setCleanupModalOpen(true)}
            >
              <tgdb-ica-icon-trash />
            </IcaButtonIcon>
          </div>
          }
          <div className='grocery-feed__search'>
            <IcaInputSearch 
              label='Sök på artikelnamn eller EAN'
              value={search}
              onInput={handleSearchChanged}
              onClear={handleSearchClear}
            />
          </div>
          <div
            className='grocery-feed__filter'
          >
            <div className='mobile-scroll-horizontal flex items-center'>
              <p className='self-center select-none my-2 mr-2 ml-0 md:ml-2'>
                Visa:
              </p>
              <div className='flex'>
                {filterOptions.map((option) => (
                  <div
                    key={option.value}
                    className='grocery-feed__quantity-filter-button'
                  >
                    <button
                      id={option.value}
                      value={option.value}
                      className={`button-radio${activeFilter === option.value ? '-active' : ''}`}
                      onClick={changeFilter}
                    >
                      {option.displayText}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {!deviceState.isMobile && (
              <div className='flex items-center gap-4'>
                <IcaButtonIcon
                  accent
                  text='Rensa i sortimentet'
                  onClick={() => setCleanupModalOpen(true)}
                >
                  <tgdb-ica-icon-trash />
                </IcaButtonIcon>
                <IcaButtonIcon
                  accent
                  text='Plocklista'
                  onClick={() => setPicklistModalOpen(true)}
                >
                  <tgdb-ica-icon-shopping-list />
                </IcaButtonIcon>
              </div>
            )}
          </div>
        </>
      )}
      <Picklist
        sites={sites}
        items={items}
        categories={categories}
        loading={picklistLoading}
        error={picklistError}
        showPicklistDialog={picklistModalOpen}
        setShowPicklistDialog={setPicklistModalOpen}
      />
      <GroceryFeedCleanup
        feed={feed}
        showCleanupDialog={cleanupModalOpen}
        setShowCleanupDialog={setCleanupModalOpen}
        onDeleteMultiple={deleteMultipleGroceries}
        loading={cleanupLoading}
      />
      {deviceState.isMobile ? (
        filteredFeed.length === 0 ? (
          <SearchPlaceholder />
        ) : (
          <GroceryFeedItemList
            groceries={filteredFeed}
            sortProps={[
              { name: 'Artikel', sortValue: (item) => item.name },
              { name: 'Ord. Pris', sortValue: (item) => item.ordinaryPrice },
              {
                name: 'Sålda',
                sortValue: (item) => item.restockedQuantity - item.quantity,
              },
              { name: 'Antal i skåp', sortValue: (item) => item.quantity },
            ]}
            saveCustomPrice={handleCustomPriceSave}
            deleteCustomPrice={handleCustomPriceDelete}
            deleteGrocery={deleteGrocery}
            onChangeStockSettings={onChangeStockSettings}
          />
        )
      ) : (
        <>      
          <div className='mt-4' style={{ '--IcaTable__header-items-top': '66px' } as React.CSSProperties}>
            {filteredFeed.length > 0 && (
              <GroceryFeedTable
                data={filteredFeed}
                selectedArticles={selectedArticles}
                allArticles={allArticles}
                onSelectArticle={handleSelectArticle}
                onSelectAll={() => setSelectedArticles(allArticles)}
                onDeselectAll={() => setSelectedArticles([])}
                onCustomPriceChange={handleCustomPriceSave}
                onCustomPriceDelete={handleCustomPriceDelete}
                onDeleteArticle={(item) => {
                  setConfirmDeleteSingleModalOpen(true)
                  setSelectedGrocery(item)
                }}
                onChangeStockSettings={onChangeStockSettings}
              />
            )}
            {filteredFeed.length === 0 && <SearchPlaceholder />}
          </div>

          {selectedArticles.length > 0 && (
            <>
              <div className='fixed left-1/2 transform -translate-x-1/2 box-border w-max bottom-[16px] flex flex-col items-center gap-2'>
                <IcaButton
                  text='Ändra gränsvärde för "Snart slut"'
                  onClick={() => setConfirmRunningOutThresholdModalOpen(true)}
                  variation="secondary"
                />
                <IcaButton
                  text='Ta bort från sortimentet'
                  onClick={() => setConfirmDeleteMultipleModalOpen(true)}
                >
                  <tgdb-ica-icon-trash slot='first' />
                </IcaButton>
              </div>
            </>
          )}
                        
          <IcaDialog
            open={confirmDeleteSingleModalOpen}
            onClose={() => setConfirmDeleteSingleModalOpen(false)}
            slot='main'
          >
            <IcaAttentionHeader label='Artikeln tas bort från varuskåpets sortiment' />
            <p className='mt-2 mx-6 mb-6 text-[#3A3A3A]'>
              Är du säker på att du vill ta bort vald artikel?
              En borttagen artikel läggs automatiskt tillbaka i sortimentet igen nästa gång du fyller på den i ett varuskåp.
            </p>
            
            <IcaButton
              slot="buttons"
              text="Ta bort"
              onClick={async () => {
                await deleteGrocery(selectedGrocery)
                setConfirmDeleteSingleModalOpen(false)
              }}
              fullwidth
            />
            <IcaButton
              slot="buttons"
              text="Avbryt"
              variation="secondary"
              onClick={() => {setConfirmDeleteSingleModalOpen(false); setSelectedGrocery(undefined)}}
              fullwidth
            />
          </IcaDialog>

          <IcaDialog
            open={confirmDeleteMultipleModalOpen}
            onClose={() => setConfirmDeleteMultipleModalOpen(false)}
            slot='main'
          >
            <IcaAttentionHeader label='Artiklarna tas bort från varuskåpets sortiment' />
            <p className='mt-2 mx-6 mb-6 text-[#3A3A3A]'>
              Är du säker på att du vill ta bort valda artiklar?
              Borttagna artiklar läggs automatiskt tillbaka i sortimentet igen nästa gång du fyller på dem i ett varuskåp.
            </p>
            
            <IcaButton
              slot="buttons"
              text="Ta bort"
              onClick={async () => {
                await deleteMultipleGroceries(selectedArticles)
                setConfirmDeleteMultipleModalOpen(false)
                setSelectedArticles([])
              }}
              fullwidth
            />
            <IcaButton
              slot="buttons"
              text="Avbryt"
              variation="secondary"
              onClick={() => setConfirmDeleteMultipleModalOpen(false) }
              fullwidth
            />
          </IcaDialog>
          <IcaDialog
            open={confirmRunningOutThresholdModalOpen}
            onClose={() => {setConfirmRunningOutThresholdModalOpen(false); setUpdatedRunningOutThreshold(1); setThresholdInputError(null)}}
          >
            <IcaHeader
              slot='header'
              variant='dialog'
              heading={`Ändra gränsvärde för ${selectedArticles.length === 1 ? '1 artikel' : `${selectedArticles.length} artiklar`}`}
              leftaction='close'
              onLeftactionclick={() => {setConfirmRunningOutThresholdModalOpen(false); setUpdatedRunningOutThreshold(1); setThresholdInputError(null) }}

            />
            <IcaSegment heading='Gränsvärde för "Snart slut"'></IcaSegment>
            <IcaCardRow noninteractive>
              <span>Antal kvar</span>
              <IcaInputNumberStepper
                min={1}
                value={updatedRunningOutThreshold}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setThresholdInputError(null)
                  const newValue = Number(event.target.value)
                  setUpdatedRunningOutThreshold(newValue)
                }}
              />
            </IcaCardRow>
            {thresholdInputError && <p className="text-[var(--color-accent-red)] bg-[var(--color-white)] px-4 py-2">{thresholdInputError}</p>}
            <IcaButton
              slot="buttons"
              text="Klar"
              onClick={async () => {
                await updateRunningOutThresholdMultipleArticles(selectedArticles)
              }}
              fullwidth
            />
            <IcaButton
              slot="buttons"
              text="Avbryt"
              variation="secondary"
              onClick={() => {setConfirmRunningOutThresholdModalOpen(false); setUpdatedRunningOutThreshold(1); setThresholdInputError(null)}}
              fullwidth
            />
          </IcaDialog>
        </>
      )}
    </>
  )
}
